/**
 * Limitations:
 * - Does not handle React hooks
 * - Does not handle React Context
 * - Does not handle dangerouslySetInnerHTML
 */
import { type ReactElement, type ReactNode, Fragment } from 'react';

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

type Props = Record<string, unknown> & { children?: ReactNode };

type RenderContext = {
  preloadImages: Set<string>;
};

const PRESERVE_CASE_ATTRS = new Set([
  'viewBox',
  'preserveAspectRatio',
  'xmlns',
  'xmlnsXlink',
  'colSpan',
  'rowSpan',
  'spellCheck',
  'contentEditable',
  'readOnly',
]);

const PROP_TO_ATTR_MAP: Record<string, string> = {
  defaultValue: 'value',
  defaultChecked: 'checked',
  autoFocus: 'autofocus',
};

const toAttrName = (key: string): string => {
  if (key === 'xmlnsXlink') return 'xmlns:xlink';
  return PRESERVE_CASE_ATTRS.has(key) ? key : key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
};

const serializeProps = (props: Props): string => {
  const attrs: string[] = [];

  for (const [key, value] of Object.entries(props)) {
    if (
      key === 'children' ||
      key === 'key' ||
      key === 'ref' ||
      key === 'dangerouslySetInnerHTML' ||
      value === undefined ||
      value === null ||
      typeof value === 'function'
    ) {
      continue;
    }

    if (
      value === false &&
      !key.startsWith('aria-') &&
      !key.startsWith('data-') &&
      key !== 'spellCheck' &&
      key !== 'contentEditable' &&
      key !== 'defaultValue'
    ) {
      continue;
    }

    if (key === 'className') {
      if (typeof value === 'string') {
        attrs.push(`class="${escapeHtml(value)}"`);
      } else if (Array.isArray(value)) {
        attrs.push(`class="${escapeHtml(value.join(','))}"`);
      } else if (typeof value === 'number') {
        attrs.push(`class="${value}"`);
      }
    } else if (key === 'htmlFor') {
      if (typeof value === 'string' || typeof value === 'number') {
        attrs.push(`for="${escapeHtml(String(value))}"`);
      }
    } else if (key === 'tabIndex') {
      if (typeof value === 'number' || typeof value === 'string') {
        attrs.push(`tabindex="${value}"`);
      }
    } else if (key === 'style' && typeof value === 'object' && value !== null) {
      const styleStr = Object.entries(value as Record<string, string | number>)
        .filter(([_k, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => {
          const cssKey = toAttrName(k);
          const isUnitless = !!k.match(
            /opacity|weight|zoom|flex|grid|order|lineHeight|zIndex|animationIterationCount/i
          );
          const cssValue = typeof v === 'number' && v !== 0 && !isUnitless ? `${v}px` : String(v);
          return `${cssKey}:${cssValue}`;
        })
        .join(';');
      if (styleStr) {
        attrs.push(`style="${escapeHtml(styleStr)}"`);
      }
    } else if (key.startsWith('aria-') || key.startsWith('data-')) {
      if (typeof value === 'boolean') {
        attrs.push(`${key}="${value}"`);
      } else if (typeof value === 'string' || typeof value === 'number') {
        attrs.push(`${key}="${escapeHtml(String(value))}"`);
      }
    } else if ((key === 'contentEditable' || key === 'spellCheck') && typeof value === 'boolean') {
      attrs.push(`${key}="${value}"`);
    } else if (PROP_TO_ATTR_MAP[key]) {
      const attrName = PROP_TO_ATTR_MAP[key];
      if (typeof value === 'boolean') {
        if (value) {
          attrs.push(`${attrName}=""`);
        } else if (key === 'defaultValue') {
          attrs.push(`${attrName}="${escapeHtml(String(value))}"`);
        }
      } else if (typeof value === 'string' || typeof value === 'number') {
        attrs.push(`${attrName}="${escapeHtml(String(value))}"`);
      }
    } else if (typeof value === 'boolean') {
      if (value) {
        attrs.push(`${toAttrName(key)}=""`);
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      attrs.push(`${toAttrName(key)}="${escapeHtml(String(value))}"`);
    }
  }

  return attrs.length > 0 ? ` ${attrs.join(' ')}` : '';
};

const renderNode = (node: ReactNode, ctx: RenderContext): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return escapeHtml(String(node));
  }

  if (Array.isArray(node)) {
    return node.map((n) => renderNode(n as ReactNode, ctx)).join('');
  }

  const element = node as ReactElement;

  if (element.type === Fragment) {
    const props = element.props as Props;
    return renderNode(props.children, ctx);
  }

  if (typeof element.type === 'function') {
    const Component = element.type as (props: Props) => ReactNode;
    const rendered = Component(element.props as Props);
    if (rendered === undefined) {
      return '';
    }
    return renderNode(rendered, ctx);
  }

  if (typeof element.type === 'object' && element.type !== null) {
    const type = element.type as {
      $$typeof?: symbol;
      type?: unknown;
      render?: (props: Props) => ReactNode;
    };
    const symbolStr = type.$$typeof?.toString();
    if (symbolStr === 'Symbol(react.forward_ref)' && typeof type.render === 'function') {
      return renderNode(type.render(element.props as Props), ctx);
    }
    if (symbolStr === 'Symbol(react.memo)') {
      if (typeof type.type === 'function') {
        const Component = type.type as (props: Props) => ReactNode;
        return renderNode(Component(element.props as Props), ctx);
      }
      if (typeof type.type === 'object') {
        const memoType = type.type as {
          $$typeof?: symbol;
          render?: (props: Props) => ReactNode;
        };
        if (memoType.$$typeof?.toString() === 'Symbol(react.forward_ref)' && typeof memoType.render === 'function') {
          return renderNode(memoType.render(element.props as Props), ctx);
        }
      }
    }
  }

  if (typeof element.type === 'string') {
    const tag = element.type;
    const props = (element.props || {}) as Props;
    const propsStr = serializeProps(props);

    if (tag === 'img' && typeof props.src === 'string') {
      ctx.preloadImages.add(props.src);
    }

    if (VOID_ELEMENTS.has(tag)) {
      return `<${tag}${propsStr}/>`;
    }

    const children = renderNode(props.children, ctx);
    return `<${tag}${propsStr}>${children}</${tag}>`;
  }

  return '';
};

export const renderToString = (element: ReactElement): string => {
  const ctx: RenderContext = { preloadImages: new Set() };
  const body = renderNode(element, ctx);

  const preloads = Array.from(ctx.preloadImages)
    .map((src) => `<link rel="preload" as="image" href="${escapeHtml(src)}"/>`)
    .join('');

  return preloads + body;
};
